import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '@/config/firebase';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import { SubmeterRespostasEtapa1Schema } from '../../models/candidato.schemas';
import type { RespostasEtapa1Candidato } from '../../models/candidato.types';
import { montarEtapasCandidato, etapaEstaCompleta } from './candidatoFormExternoPage.definitions';
import { EtapaDadosPessoais, EtapaSobreIA, EtapaPerfilProfissional, EtapaAutorizacao } from './components';

interface CandidatoExternoData {
  nome: string;
  perguntasSobreIA: boolean;
}

const RESPOSTAS_INICIAIS: RespostasEtapa1Candidato = {
  nomeCompleto: '',
  dataNascimento: '',
  rg: '',
  // Órgão emissor, UF e data de emissão do RG passaram pra Etapa 2 — vão vazios aqui.
  orgaoEmissorRg: null,
  orgaoEmissorRgUf: null,
  dataEmissaoRg: null,
  cpf: '',
  cidadeNascimento: '',
  ufNascimento: '',
  cidadeEstadoNascimento: '',
  cidadeEstadoResidencia: '',
  nomeMae: '',
  nomePai: '',
  sobreIaEtapas: '',
  sobreIaProcesso: '',
  sobreIaQualidade: '',
  disponibilidadeInicio: '',
  pretensaoSalarial: '',
  autorizacaoLgpd: false,
};

const variants = {
  enter: { x: 50, opacity: 0, scale: 0.95 },
  center: { x: 0, opacity: 1, scale: 1, zIndex: 1, transition: { duration: 0.4, ease: 'easeInOut' } },
  exit: { x: -50, opacity: 0, scale: 0.95, filter: 'blur(10px)', zIndex: 0, transition: { duration: 0.3, ease: 'easeIn' } },
} as const;

/**
 * Formulário público (sem login) preenchido pelo próprio candidato — mesmo padrão da
 * Avaliação Externa (`ExternalEvaluationPage`): token de uso único na URL, chamado direto
 * via Cloud Functions `onCall` sem `request.auth`, dentro do próprio Digital (não é uma
 * ferramenta externa tipo Microsoft Forms). Estilo/wizard passo a passo replicado do
 * mesmo componente, adaptado pra perguntas de texto/data em vez de escolha única.
 */
export default function CandidatoFormExternoPage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [dados, setDados] = useState<CandidatoExternoData | null>(null);
  const [etapaAtual, setEtapaAtual] = useState(0);
  const [respostas, setRespostas] = useState<RespostasEtapa1Candidato>(RESPOSTAS_INICIAIS);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  useEffect(() => {
    if (!token) return;
    const functions = getFunctions(app, 'us-central1');
    const getCandidatoExterno = httpsCallable<{ token: string }, CandidatoExternoData>(
      functions,
      'getCandidatoExterno'
    );
    getCandidatoExterno({ token })
      .then((result) => setDados(result.data))
      .catch((err: any) => setErro(err.message || 'Erro ao carregar o formulário.'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    document.getElementById('wizard-content-stage')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [etapaAtual]);

  const handleChange = (campo: keyof RespostasEtapa1Candidato, valor: string | boolean) => {
    setRespostas((atual) => ({ ...atual, [campo]: valor }));
  };

  const handleEnviar = async () => {
    if (!token) return;

    // "Cidade - UF" junto continua indo — uma function mais antiga ainda publicada exige esse
    // campo (foi exatamente esse tipo de descompasso front × function que travou candidatos).
    const parsed = SubmeterRespostasEtapa1Schema.safeParse({
      ...respostas,
      cidadeEstadoNascimento: [respostas.cidadeNascimento?.trim(), respostas.ufNascimento?.trim()]
        .filter(Boolean)
        .join(' - '),
    });
    if (!parsed.success) {
      setErro(parsed.error.issues[0]?.message ?? 'Preencha todos os campos obrigatórios.');
      return;
    }

    setEnviando(true);
    try {
      const functions = getFunctions(app, 'us-central1');
      const submitCandidatoExterno = httpsCallable(functions, 'submitCandidatoExterno');
      await submitCandidatoExterno({ token, respostas: parsed.data });
      setEnviado(true);
    } catch (err: any) {
      setErro(err.message || 'Erro ao enviar o formulário.');
    } finally {
      setEnviando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (erro && !enviado) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-lg border border-red-100 bg-red-50 p-6 shadow-sm">
          <h2 className="mb-2 text-lg font-bold text-red-800">Não foi possível continuar</h2>
          <p className="text-red-600">{erro}</p>
        </div>
      </div>
    );
  }

  if (enviado) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-2xl rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-lg md:p-12">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
            <CheckCircleIcon className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="mb-4 text-2xl font-bold text-gray-900">Formulário enviado com sucesso!</h2>
          <p className="text-gray-600">Obrigado por participar do nosso processo seletivo.</p>
        </div>
      </div>
    );
  }

  const etapas = montarEtapasCandidato(dados?.perguntasSobreIA ?? false);
  const etapa = etapas[etapaAtual];
  const ultimaEtapa = etapaAtual === etapas.length - 1;
  const progresso = ((etapaAtual + 1) / etapas.length) * 100;
  const podeAvancar = etapaEstaCompleta(etapa, respostas);

  return (
    <div className="min-h-screen bg-gray-50 pb-20 font-sans text-gray-900">
      <div className="bg-indigo-900 p-6 text-center text-white shadow-md">
        <h1 className="text-2xl font-bold">Processo Seletivo</h1>
        <p className="opacity-80">Olá, {dados?.nome}!</p>
      </div>

      <div id="wizard-content-stage" className="relative mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 px-2">
          <div className="mb-2 flex items-end justify-between">
            <span className="text-sm font-semibold text-indigo-900">
              Etapa {etapaAtual + 1} de {etapas.length}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full bg-indigo-600 transition-all duration-500 ease-out"
              style={{ width: `${progresso}%` }}
            />
          </div>
        </div>

        <AnimatePresence initial={false} mode="wait">
          <motion.div
            key={etapaAtual}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            className="w-full overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg"
          >
            <div className="p-8 md:p-10">
              {etapa === 'introducao' && (
                <>
                  <h2 className="mb-4 text-2xl font-bold text-gray-900">Bem-vindo(a)!</h2>
                  <p className="text-gray-600">
                    Estamos muito felizes com o seu interesse em fazer parte da Raro Labs. Vamos precisar de
                    algumas informações pra seguir no processo seletivo — leva poucos minutos.
                  </p>
                </>
              )}
              {etapa === 'dados_pessoais' && <EtapaDadosPessoais respostas={respostas} onChange={handleChange} />}
              {etapa === 'sobre_ia' && <EtapaSobreIA respostas={respostas} onChange={handleChange} />}
              {etapa === 'perfil_profissional' && (
                <EtapaPerfilProfissional respostas={respostas} onChange={handleChange} />
              )}
              {etapa === 'autorizacao' && (
                <EtapaAutorizacao
                  autorizacaoLgpd={respostas.autorizacaoLgpd}
                  onChange={(valor) => handleChange('autorizacaoLgpd', valor)}
                />
              )}
              {erro && <p className="mt-4 text-sm text-red-600">{erro}</p>}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-8 flex items-center justify-between px-2">
          <button
            onClick={() => setEtapaAtual((atual) => Math.max(0, atual - 1))}
            disabled={etapaAtual === 0}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              etapaAtual === 0 ? 'cursor-not-allowed text-gray-300' : 'text-gray-600 hover:bg-white hover:text-indigo-600'
            }`}
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Anterior
          </button>

          <button
            onClick={() => (ultimaEtapa ? handleEnviar() : setEtapaAtual((atual) => atual + 1))}
            disabled={!podeAvancar || enviando}
            className={`flex items-center gap-2 rounded-full px-8 py-3 font-bold shadow-lg shadow-indigo-200 transition-all ${
              !podeAvancar || enviando
                ? 'cursor-not-allowed bg-gray-200 text-gray-400 shadow-none'
                : 'bg-indigo-600 text-white hover:scale-105 hover:bg-indigo-700 active:scale-95'
            }`}
          >
            {enviando ? 'Enviando...' : etapa === 'introducao' ? 'Começar' : ultimaEtapa ? 'Finalizar' : 'Próximo'}
          </button>
        </div>
      </div>
    </div>
  );
}
